import { DialogFormFieldContainer } from '@/common/dialog/DialogFormFieldContainer';
import { Form } from '@/common/form/Form';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { TreeUtils } from '@/common/utils/TreeUtils';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, styled } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

export const MassOverwriteDialog = ({
  kpiCodes,
  closeDialog,
  entity: entityProp,
  defaultDimValues
}) => {

  const module = useModuleFromRoute();

  const routeEntity = useEntityFromRoute();
  const entity = entityProp ?? routeEntity;

  const rsrc = useRsrc();

  const perspectiveDef = useRef();

  const dimFieldToFieldDefRef = useRef({});
  const kpiFieldToFieldDefRef = useRef({});
  const fieldDefByFieldRef = useRef({});

  const [step, setStep] = useState(0);

  const dimsFormRef = useRef();
  const [dimFormFieldDefs, setDimFieldFormDefs] = useState();
  const [dimFormValues, setDimFormValues] = useState({});

  const rowFormRefs = useRef({});
  const [rows, setRows] = useState([]);

  const [selectedKpiCode, setSelectedKpiCode] = useState(() => (kpiCodes[0]));

  useEffect(() => {
    if (!selectedKpiCode) {
      setSelectedKpiCode(kpiCodes[0]);
    }
    
  }, [kpiCodes, selectedKpiCode]);

  const actionPluginCode = 'MASS_OVERWRITE_ACTION_PLUGIN';

  useEffect(() => {
    Promise.all([
      rsrc.data(module, 'PERSPECTIVE_DEF_DATA_PLUGIN', { entity }, entity),
      rsrc.fieldDefs(module, entity),
      rsrc.pluginFieldDefs(module, actionPluginCode, entity)
    ]).then(([perspectiveData, fieldDefs, pluginFieldDefs]) => {
      perspectiveDef.current = perspectiveData;

      const fieldDefByField = Object.fromEntries(fieldDefs.map(fieldDef => [fieldDef.field, fieldDef]));
      fieldDefByFieldRef.current = fieldDefByField;

      const dimFieldToFieldDef = {};
      Object.entries(perspectiveData.codeToDim).forEach(([dimCode, dim]) => {
        if (defaultDimValues?.[dimCode]) {
          return;
        }

        const field = `${StringUtils.toLowerCamel(dimCode)}Id`;
        const fieldDef = fieldDefByField[field];
        if (!fieldDef) {
          return;
        }

        const leafIds = TreeUtils.parseTree(dim.tree).leafIds;
        fieldDef.expr = ExprUtils.whereParams('id', leafIds);
        fieldDef.required = true;
        dimFieldToFieldDef[field] = fieldDef;
      });

      dimFieldToFieldDefRef.current = dimFieldToFieldDef;

      setRows([{ values: {} }]);

      const dimFieldDefs = Object.values(dimFieldToFieldDef).map(dimFieldDef => ({
        ...dimFieldDef,
        required: false
      }));

      setDimFieldFormDefs([
        ...pluginFieldDefs,
        ...dimFieldDefs
      ]);
    });
  }, [module, entity]);

  useEffect(() => {
    if (!selectedKpiCode || !dimFormFieldDefs) {
      return;
    }

    const kpiLowerCamel = StringUtils.toLowerCamel(selectedKpiCode);
    const kpiFieldDef = fieldDefByFieldRef.current[kpiLowerCamel];
    if (kpiFieldDef) {
      kpiFieldDef.required = true;
      kpiFieldDef.precedence = 1000; // Always last
      kpiFieldToFieldDefRef.current = { [kpiLowerCamel]: kpiFieldDef };
    } else {
      kpiFieldToFieldDefRef.current = {};
    }

    setRows([{ values: {} }]);
  }, [selectedKpiCode, dimFormFieldDefs]);

  const rowFieldDefs = useMemo(() => {
    if (!dimFormFieldDefs) return;

    const dimFieldToFieldDef = dimFieldToFieldDefRef.current;
    const kpiFieldToFieldDef = kpiFieldToFieldDefRef.current;
    const nextFieldDefs = { ...kpiFieldToFieldDef };

    Object.entries(dimFieldToFieldDef).forEach(([fieldId, fieldDef]) => {
      if (!dimFormValues[fieldId]) {
        nextFieldDefs[fieldId] = fieldDef;
      }
    });

    return nextFieldDefs;
  }, [dimFormFieldDefs, dimFormValues, selectedKpiCode]);

  const rowFieldKeys = useMemo(() => {
    if (!rowFieldDefs) return [];
    return Object.keys(rowFieldDefs);
  }, [rowFieldDefs]);

  const sortedRowFieldDefs = useMemo(() => {
    if (!rowFieldDefs) return [];
    return Object.values(rowFieldDefs).sort(
      (a, b) => (a.precedence ?? 0) - (b.precedence ?? 0)
    );
  }, [rowFieldDefs]);

  const setRowFormValues = (index) => (nextValuesOrUpdater) => {
    setRows(rows => {
      const currentRow = rows[index] ?? { values: {} };
      const currentValues = currentRow.values ?? {};
      const nextValues = typeof nextValuesOrUpdater === 'function'
        ? nextValuesOrUpdater(currentValues)
        : nextValuesOrUpdater;

      const newRows = [...rows];
      newRows[index] = { ...currentRow, values: nextValues };

      if (!rowFieldDefs) {
        return newRows;
      }

      const isLastRow = index === newRows.length - 1;
      if (!isLastRow) {
        return newRows;
      }

      const isComplete = rowFieldKeys.every((fieldId) => {
        const value = nextValues[fieldId];
        return value != null;
      });

      if (isComplete) {
        newRows.push({ values: {} });
      }

      return newRows;
    });
  };

  const clickNext = () => {
    if (dimsFormRef.current.hasErrorState()) {
      dimsFormRef.current.touchAll();
      return;
    }

    setStep(1);
  }

  const clickPrev = () => {
    setStep(0);
  }

  const isBlank = (rowValues) => {
    if (!rowFieldKeys.length) {
      return true;
    }

    return rowFieldKeys.every((fieldId) => {
      const value = rowValues?.[fieldId];
      return value == null;
    });
  }

  const submit = () => {
    let submit = true;
    Object.entries(rowFormRefs.current).forEach(([indexStr, rowFormValues]) => {
      const index = Number(indexStr);
      const isLast = index === rows.length - 1;

      if (isLast && isBlank(rows[index]?.values)) {
        return;
      }

      if (rowFormValues.hasErrorState()) {
        rowFormValues.touchAll();
        submit = false;
        return;
      }

    });

    if (!submit) {
      return;
    }

    const actionReq = {
      comment: dimFormValues.comment,
      dims: dimFormValues,
      defaultDimValues,
      kpiCode: selectedKpiCode,
      rows
    };

    const job = {
      code: actionPluginCode.replace('_ACTION_PLUGIN', ''),
      module,
      entity,
      rsrcCode: actionPluginCode,
      reqParamJson: JSON.stringify(actionReq),
      cat: 'ACTION'
    }

    rsrc.save('skdr', 'JOB', job).then(job => {
      rsrc.start(job.id);
      closeDialog();
    });
  }

  const width = step === 0 ? '60%' : '85%';

  return (
    <Dialog open fullWidth onClose={() => closeDialog()}
      slotProps={{
        paper: { sx: { width, maxWidth: width, display: 'flex', flexDirection: 'column' } }
      }}
    >
      {perspectiveDef.current && selectedKpiCode && (
        <DialogTitle>{perspectiveDef.current.codeToKpi[selectedKpiCode].displayName} Overwrite</DialogTitle>
      )}
      <DialogContent>
        <Stepper activeStep={step}>
          <Step><StepLabel>Select Dimensions</StepLabel></Step>
          <Step><StepLabel>Add Adjustments</StepLabel></Step>
        </Stepper>
        <Box sx={{ mt: 1, mb: 1 }}>
          <DialogContentText>
            {step === 0
              ? 'Choose which dimensions to apply to all adjustments.'
              : 'Add one or more adjustment rows and fill in all required fields.'}
          </DialogContentText>
        </Box>
        {/* Dimensions Form */}
        {step === 0 && selectedKpiCode && dimFormFieldDefs && 
          <>
            {/* KPI Selection */}
            {kpiCodes.length > 1 && perspectiveDef.current && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>KPI</InputLabel>
                  <Select
                    label="KPI"
                    value={selectedKpiCode}
                    onChange={(event) => setSelectedKpiCode(event.target.value)}
              >
                {kpiCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {perspectiveDef.current.codeToKpi[code]?.displayName ?? code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
          <Form
            ref={dimsFormRef}
            fieldDefs={dimFormFieldDefs}
            formValues={dimFormValues}
            setFormValues={setDimFormValues}
            container={DialogFormFieldContainer}
          />
        </>
        }
        {/* Rows Form */}
        {step === 1 && rowFieldDefs && rows.map((row, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: index === 0 ? 0 : 1 }}
          >
            {/* Row's Field Defs */}
            {/* TODO: INVESTIGATE OTHER WAYS TO EST THE REF */}
            <Form
              ref={(api) => {
                if (api) rowFormRefs.current[index] = api;
                else delete rowFormRefs.current[index];
              }}
              fieldDefs={sortedRowFieldDefs}
              formValues={row.values}
              setFormValues={setRowFormValues(index)}
              container={OneLineFormFieldContainer}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} data-test-id="cancel-button">Cancel</Button>
        {step === 0 && 
          <Button onClick={clickNext} data-test-id="next-button">Next</Button>
        }
        {step === 1 && <>
          <Button onClick={clickPrev} data-test-id="prev-button">Previous</Button>
          <Button onClick={submit} data-test-id="submit-button">Submit</Button>
        </>}
      </DialogActions>
    </Dialog >
  )
}

export const OneLineFormFieldContainer = styled(Box)({
  display: 'grid',
  gap: '1rem',
  width: '100%',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  alignItems: 'center',
});